import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { getStringOrPlaceholder, maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { storePath } from "../../../../store/urls";
import { ReportsUrl } from "../../../overview/urls";
import { statusFormatter } from "../../../utils/misc";
import { reportsDownload } from "../../../utils/utils";
import StoresListPage from "../../components/StoresPage";
import {
  useInitialStoresReportFilterDataQuery,
  useStoresQuery
} from "../../queries";
import {
  StoresListUrl,
  StoresListUrlDialog,
  StoresListUrlQueryParams
} from "../../url";
import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
interface StoresListProps {
  params: StoresListUrlQueryParams;
}

export const StoresList: React.FC<StoresListProps> = ({ params }) => {
  const navigate = useNavigator();
  const paginate = usePaginator();
  const notify = useNotifier();
  const shop = useShop();

  const intl = useIntl();
  const dateNow = Moment(new Date()).format("YYYYMMDDHHmmss");

  const sheetName = "Store Details";
  const fileName = `${reportsDownload.Stores.filename}_${dateNow}.xlsx`;
  const currencySymbol = shop?.defaultCurrency || "PHP";

  const [loadData, setLoadData] = useState(true);
  const [downloadPagination, setDownloadPagination] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");

  const { updateListSettings, settings } = useListSettings(
    ListViews.STORE_REPORT_STOCKS
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  React.useEffect(() => {
    if (params.query || params.storeStatus) {
      if (params?.brand === undefined && params?.mall === undefined) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Please select a filter for mall or brand."
          })
        });
        navigate(StoresListUrl());
      }
    }
  }, [params.query, params.storeStatus]);

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const reportVariables = React.useMemo(
    () => ({
      ...paginationState,
      after: downloadPagination,
      filter: getFilterVariables(params),
      first: 1000
    }),
    [params]
  );

  const { data: initialFilterData } = useInitialStoresReportFilterDataQuery({
    variables: {
      brand: params.brand,
      mall: params.mall
    }
  });

  React.useEffect(
    () =>
      navigate(
        StoresListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  // Todo - <Marco>: Explore on how to fix from BE
  React.useEffect(() => {
    if (params.query !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const searchBrand = useBrandSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });
  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const tabs = getFilterTabs();

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: StoresListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    StoresListUrlDialog,
    StoresListUrlQueryParams
  >(navigate, StoresListUrl, params);

  const handleTabChange = (tab: number) => {
    navigate(
      StoresListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };

  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(StoresListUrl());
  };

  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };

  const { data: previewData } = useStoresQuery({
    displayLoader: true,
    skip: Object.keys(params).length === 0,
    variables: previewVariables
  });

  const { data: reportData, loading: reportLoading } = useStoresQuery({
    displayLoader: true,
    skip: loadData,
    variables: reportVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    previewData?.storeDetails?.pageInfo,
    paginationState,
    params
  );

  const filterOpts = getFilterOpts(
    params,
    {
      initial: maybe(
        () => initialFilterData.brands.edges.map(edge => edge.node) || []
      ),
      search: searchBrand
    },
    {
      initial: maybe(
        () => initialFilterData.malls.edges.map(edge => edge.node) || []
      ),
      search: searchMall
    }
  );

  const selectReportData = reportData => {
    const selectedData = reportData?.map(data => {
      delete data.__typename;
      delete data.id;

      data.store_status = statusFormatter(data?.store_status);
      return data;
    });
    return selectedData;
  };

  const [downloadData, setDownloadData] = useState([]);

  const shownStores = previewData?.storeDetails?.edges.map(edge => edge.node);
  const allStores = reportData?.storeDetails?.edges.map(edge => edge.node);
  const nextPage = reportData?.storeDetails?.pageInfo;

  React.useEffect(() => {
    if (reportData && !reportLoading) {
      setDownloadData(current => [...current, allStores]);
      setDownloadPagination(nextPage?.endCursor);
      if (!nextPage?.hasNextPage && nextPage?.endCursor === null) {
        const selectedReportData = selectReportData(downloadData?.flat());
        useDownloadReport(
          selectedReportData,
          reportsDownload.Stores.fields,
          sheetName,
          fileName
        );
        setDownloadStatus("success");
        setDownloadPagination("");
        setLoadData(true);
        setDownloadData([]);
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Store Details successfully downloaded."
          })
        });
        closeModal();
      }
    }
  }, [reportData]);

  const cancelDownload = () => {
    setLoadData(true);
    setDownloadData([]);
    setDownloadStatus("default");
    setDownloadPagination("");
    closeModal();
  };

  return (
    <>
      <StoresListPage
        stores={shownStores}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onFilterChange={changeFilters}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={reportLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        settings={settings}
        onBack={() => navigate(ReportsUrl)}
        download={() => openModal("download")}
        onRowClick={id => () => window.open(storePath(id))}
      />
      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setLoadData(false);
          setDownloadStatus("loading");
          setDownloadData([]);
          setDownloadPagination("");
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Stores",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download a stores with {total} rows. Do you want to continue?"
            values={{
              total: <strong>{previewData?.storeDetails?.totalCount}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />

      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={getStringOrPlaceholder(tabs[currentTab - 1]?.name)}
      />
    </>
  );
};

export default StoresList;
